import React, { useEffect, useState } from 'react';
import { CustomTableCell } from '../../../../../../components/Commons/Tables/styles';
import {
  IFutureDelivery,
  IOpenOrderData,
  IOpenOrderQuantities,
  EOrderTab,
  EOpenColumn,
  EMaterialOpenColumn
} from '../../../../../../store/ducks/long-steel-orders/types';
import moment from 'moment';
import ExpandedOpenOrderRowContent from './ExpandedOpenOrderRowContent';
import ProductStatusBarColumn from './ProductStatusBarColumn';
import {
  ECustomerTypeView,
  IUserOrderItemPreference,
  IUserPreference,
  EUserPreferenceType
} from '../../../../../../store/ducks/user/types';
import { uppercaseOnlyFirstLetter } from '../../../../../../utils/formatStrings';
import EstimatedDeliveryDateColumn from './EstimatedDeliveryDateColumn';
import DeliveryStatusColumn from './DeliveryStatusColumn';
import ExportOrders from '../../ExportOrders';
import {
  Container,
  TruckIcon,
  TableHeaderContent,
  ButtonContainer,
  SelectionContainer,
  TableContainer
} from './styles';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from '../../../../../../store';
import ConfigTables from '../../ConfigTables';
import Table from '../../../../../../components/Table';
import { Button, Tooltip } from '@gerdau/hefesto';
import TruckImg from '../../../../../../assets/images/cancelOrderTruck.svg';
import CancelOrder from './CancelOrder/';
import { TableRowSelection } from 'antd/lib/table/interface';
import UpdateOrderSideSheet from './UpdateOrder';
import { addToast } from '../../../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../../../store/ducks/toast/types';
import { sendTagging } from '../../../../../../store/ducks/tagging/actions';
import { formatDate } from '../../../../../../utils/formatDate';

interface IOpenOrdersTableProps {
  filteredOrders: IOpenOrderData[];
  userOrderPreference: IUserOrderItemPreference;
  customerViewType: ECustomerTypeView;
}

const defaultOrderUserPreference: IUserPreference = {
  name: EUserPreferenceType.ORDERS,
  ordersItens: {
    idOrder: '0',
    idProduct: '0'
  }
};

export interface IOpenOrdersTableDataSource {
  key: React.Key;
  item: IOpenOrderData;
  emitter: string;
  salesOrder: string; // OV Gerdau
  salesOrderItem: string; // Nº Item
  // OU
  customerOrder: string; // Nº Pedido Cliente
  sku: string; // Cód. material Gerdau
  // OU
  customerSKU: string; // Cód. material cliente
  materialDescription: string; // Descrição do produto
  quantities: IOpenOrderQuantities; // Status - Volumes
  requestedDate: moment.Moment; // Data solicitada
  futureDeliveries: IFutureDelivery[]; // Estimativa de entrega
  creationDate: string; // Data de criação
}

const OpenOrdersTable: React.FC<IOpenOrdersTableProps> = ({
  filteredOrders,
  userOrderPreference,
  customerViewType
}) => {
  const [openOrdersData, setOpenOrdersData] = useState<
    IOpenOrdersTableDataSource[]
  >([]);
  const [cancelOrder, setCancelOrder] = useState<IOpenOrderData | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isUpdateSheetOpened, setIsUpdateSheetOpened] =
    useState<boolean>(false);
  const dispatch = useDispatch();

  const isViewerEmitter = customerViewType === ECustomerTypeView.EMITTER;

  const orderUserPreference =
    useSelector(
      (state: ApplicationState) =>
        state.user.preferences.find(
          (pref) => pref.name === 'orders'
        ) as IUserPreference
    ) || defaultOrderUserPreference;

  const convertFilteredOrdersToIOpenOrdersTableDataSource = (
    dataInvoices: IOpenOrderData[]
  ) => {
    return dataInvoices.map((item) => ({
      key: item.id,
      item,
      emitter: `${uppercaseOnlyFirstLetter(item.soldTo.name.split(' ')[0])} - ${
        item.soldTo.code
      }`,
      salesOrder: item.salesOrder.toString(),
      salesOrderItem: item.salesOrderItem.toString(),
      customerOrder: item.customerOrder,
      sku: item.sku.toString(),
      customerSKU: item.customerSKU,
      materialDescription: item.materialDescription,
      quantities: item.quantities,
      requestedDate: moment(item.requestedDate, 'YYYY-MM-DD'),
      futureDeliveries: item.futureDeliveries,
      creationDate: item.itemCreationDate
    }));
  };

  const sortOpenOrders = (openOrders: IOpenOrdersTableDataSource[]) => {
    return openOrders.sort((current, next) => {
      return current.creationDate > next.creationDate
        ? -1
        : current.creationDate < next.creationDate
        ? 1
        : 0;
    });
  };

  useEffect(() => {
    const openOrders =
      convertFilteredOrdersToIOpenOrdersTableDataSource(filteredOrders);
    setOpenOrdersData(sortOpenOrders(openOrders));
  }, [filteredOrders]);

  const getColumns = () => {
    const columns = [
      {
        title: 'Emissor',
        dataIndex: 'emitter',
        sorter: (
          a: IOpenOrdersTableDataSource,
          b: IOpenOrdersTableDataSource
        ) => (a.emitter < b.emitter ? -1 : a.emitter > b.emitter ? 1 : 0),
        render: (salesOrder: string) => (
          <CustomTableCell>{salesOrder}</CustomTableCell>
        )
      },
      {
        title: 'OV Gerdau',
        dataIndex: 'salesOrder',
        sorter: (
          a: IOpenOrdersTableDataSource,
          b: IOpenOrdersTableDataSource
        ) => Number(a.salesOrder) - Number(b.salesOrder),
        render: (salesOrder: string) => (
          <CustomTableCell>{salesOrder}</CustomTableCell>
        )
      },
      {
        title: 'Nº item',
        dataIndex: 'salesOrderItem',
        sorter: (
          a: IOpenOrdersTableDataSource,
          b: IOpenOrdersTableDataSource
        ) => Number(a.salesOrderItem) - Number(b.salesOrderItem),
        render: (salesOrderItem: string) => (
          <CustomTableCell>{salesOrderItem}</CustomTableCell>
        )
      },
      {
        title: 'Nº pedido cliente',
        dataIndex: 'customerOrder',
        sorter: (
          a: IOpenOrdersTableDataSource,
          b: IOpenOrdersTableDataSource
        ) =>
          a.customerOrder < b.customerOrder
            ? -1
            : a.customerOrder > b.customerOrder
            ? 1
            : 0,
        render: (customerOrder: string) => (
          <CustomTableCell>{customerOrder}</CustomTableCell>
        )
      },
      {
        title: 'Material Gerdau',
        dataIndex: 'sku',
        sorter: (
          a: IOpenOrdersTableDataSource,
          b: IOpenOrdersTableDataSource
        ) => Number(a.sku) - Number(b.sku),
        render: (sku: string) => <CustomTableCell>{sku}</CustomTableCell>
      },
      {
        title: 'Material cliente',
        dataIndex: 'customerSKU',
        sorter: (
          a: IOpenOrdersTableDataSource,
          b: IOpenOrdersTableDataSource
        ) =>
          a.customerSKU < b.customerSKU
            ? -1
            : a.customerSKU > b.customerSKU
            ? 1
            : 0,
        render: (customerSKU: string) => (
          <CustomTableCell>{customerSKU ? customerSKU : ' - '}</CustomTableCell>
        )
      },
      {
        title: 'Descrição do produto',
        dataIndex: 'materialDescription',
        sorter: (
          a: IOpenOrdersTableDataSource,
          b: IOpenOrdersTableDataSource
        ) =>
          a.materialDescription < b.materialDescription
            ? -1
            : a.materialDescription > b.materialDescription
            ? 1
            : 0,
        render: (materialDescription: string) => (
          <CustomTableCell>{materialDescription}</CustomTableCell>
        )
      },
      {
        title: 'Status do produto',
        dataIndex: 'quantities',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (_: any, data: IOpenOrdersTableDataSource) => (
          <CustomTableCell>
            <ProductStatusBarColumn quantities={data.item.quantities} />
          </CustomTableCell>
        )
      },
      {
        title: (
          <>
            Criação
            <br /> do pedido
          </>
        ),
        dataIndex: 'creationDate',
        sorter: (
          a: IOpenOrdersTableDataSource,
          b: IOpenOrdersTableDataSource
        ) =>
          a.creationDate > b.creationDate
            ? -1
            : a.creationDate < b.creationDate
            ? 1
            : 0,
        render: (creationDate: string) => (
          <CustomTableCell>
            {formatDate(creationDate, 'DD/MM/YYYY')}
          </CustomTableCell>
        )
      },
      {
        title: 'Solicitado',
        dataIndex: 'requestedDate',
        sorter: (
          a: IOpenOrdersTableDataSource,
          b: IOpenOrdersTableDataSource
        ) =>
          a.requestedDate.toDate().getTime() -
          b.requestedDate.toDate().getTime(),
        render: (requestedDate: moment.Moment) => (
          <CustomTableCell>
            {requestedDate.format('DD/MM/YYYY')}
          </CustomTableCell>
        )
      },
      {
        title: (
          <>
            Período de
            <br /> entrega
          </>
        ),
        dataIndex: 'futureDeliveries',
        sorter: (
          a: IOpenOrdersTableDataSource,
          b: IOpenOrdersTableDataSource
        ) => {
          const dateA =
            a.futureDeliveries.length > 0
              ? a.futureDeliveries[0].estimatedDeliveryDate
              : undefined;
          const dateB =
            b.futureDeliveries.length > 0
              ? b.futureDeliveries[0].estimatedDeliveryDate
              : undefined;

          const getDateTime = (date: string): number => {
            const dateFormat = 'DD/MM/YYYY';
            let dateTime = 0;
            if (date.length > 10) {
              // caso retorne no formato "DD/MM/YYYY à DD/MM/YYYY"
              const splitedDate = date.split(' ');
              dateTime = moment(splitedDate[splitedDate.length - 1], dateFormat)
                .toDate()
                .getTime();
            } else {
              dateTime = moment(date, dateFormat).toDate().getTime();
            }
            return dateTime;
          };

          if (dateA && dateB) {
            return getDateTime(dateA) - getDateTime(dateB);
          } else if (dateA && !dateB) {
            return -1;
          } else if (!dateA && dateB) {
            return 1;
          } else {
            return 0;
          }
        },
        render: (futureDeliveries: IFutureDelivery[]) => (
          <CustomTableCell>
            <EstimatedDeliveryDateColumn futureDeliveries={futureDeliveries} />
          </CustomTableCell>
        )
      },
      {
        title: 'Status da entrega',
        dataIndex: 'futureDeliveries',
        sorter: (
          a: IOpenOrdersTableDataSource,
          b: IOpenOrdersTableDataSource
        ) => {
          const dateA =
            a.futureDeliveries.length > 0
              ? a.futureDeliveries[0].estimatedDeliveryDate
              : undefined;
          const dateB =
            b.futureDeliveries.length > 0
              ? b.futureDeliveries[0].estimatedDeliveryDate
              : undefined;

          const getDateTime = (date: string): number => {
            const dateFormat = 'DD/MM/YYYY';
            let dateTime = 0;
            if (date.length > 10) {
              // caso retorne no formato "DD/MM/YYYY à DD/MM/YYYY"
              const splitedDate = date.split(' ');
              dateTime = moment(splitedDate[splitedDate.length - 1], dateFormat)
                .toDate()
                .getTime();
            } else {
              dateTime = moment(date, dateFormat).toDate().getTime();
            }
            return dateTime;
          };

          if (dateA && dateB) {
            return getDateTime(dateA) - getDateTime(dateB);
          } else if (dateA && !dateB) {
            return -1;
          } else if (!dateA && dateB) {
            return 1;
          } else {
            return 0;
          }
        },
        render: (futureDeliveries: IFutureDelivery[]) => (
          <DeliveryStatusColumn
            estimatedDeliveryDate={
              futureDeliveries.length > 0
                ? futureDeliveries[0].estimatedDeliveryDate
                : undefined
            }
          />
        )
      },
      ...(isViewerEmitter
        ? [
            {
              title: '',
              dataIndex: 'cancelOrderBtn',
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              render: (_: any, data: IOpenOrdersTableDataSource) => (
                <div className="truck-btn-cancel-order">
                  <Button
                    name="cancel-order-btn"
                    variant="none"
                    onClick={() => setCancelOrder(data.item)}
                  >
                    <TruckIcon src={TruckImg} alt="cancel-order-icon" />
                  </Button>
                </div>
              )
            }
          ]
        : [])
    ];

    const hideColumns: string[] = [];

    if (userOrderPreference.idOrder === EOpenColumn.ORDER_NUMBER) {
      hideColumns.push('salesOrder'); // OV Gerdau
      hideColumns.push('salesOrderItem'); // Nº item
    } else {
      hideColumns.push('customerOrder'); // Nº Pedido Cliente
    }

    if (userOrderPreference.idProduct === EMaterialOpenColumn.GERDAU_MATERIAL) {
      hideColumns.push('customerSKU'); // Cód. material cliente
    } else {
      hideColumns.push('sku'); // Cód. material Gerdau
    }

    if (
      customerViewType === ECustomerTypeView.RECEIVER ||
      customerViewType === ECustomerTypeView.FABRICATION_RECEIVER
    ) {
      hideColumns.push('emitter'); // Emissor
    }

    return columns.filter((col) => !hideColumns.includes(col.dataIndex));
  };

  const renderExpandableSection = (record: IOpenOrdersTableDataSource) => {
    return (
      <ExpandedOpenOrderRowContent
        item={record.item}
        userOrderPreference={userOrderPreference}
        customerViewType={customerViewType}
      />
    );
  };

  const onUpdateOrderClick = () => {
    if (selectedRowKeys.length === 0) {
      dispatch(
        addToast({
          message:
            'Para solicitar uma alteração, selecione pelo menos um item.',
          type: EToastTypes.WARNING
        })
      );
    } else {
      dispatch(
        sendTagging({
          screenName: 'LG:PainelGestao:Selecao',
          itemDescription: 'pedidosemaberto_solicitaralteracao',
          itemName: 'button:solicitaralteracao',
          event: 'click'
        })
      );
      setIsUpdateSheetOpened(true);
    }
  };

  const onCloseSideSheet = () => {
    setIsUpdateSheetOpened(false);
  };

  const rowSelection: TableRowSelection<IOpenOrdersTableDataSource> = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    type: 'checkbox',
    preserveSelectedRowKeys: true,
    renderCell: (_value, record, _index, originNode) => {
      return record.item.orderType !== 'Longos' ? (
        <Tooltip
          title="Esse item não pode ser selecionado no momento. Consulte o seu vendedor."
          placement="right"
        >
          <div>{originNode}</div>
        </Tooltip>
      ) : (
        <>{originNode}</>
      );
    },
    hideSelectAll: true,
    getCheckboxProps: (record) => {
      if (selectedRowKeys.length >= 5) {
        return {
          disabled: !selectedRowKeys.includes(record.key)
        };
      } else if (record.item.orderType !== 'Longos') {
        return {
          disabled: true
        };
      }
      return {
        disabled: false
      };
    }
  };

  return (
    <>
      {filteredOrders.length > 0 && (
        <Container>
          <TableHeaderContent>
            <SelectionContainer>
              {isViewerEmitter ? (
                <>
                  <span>
                    {selectedRowKeys.length}{' '}
                    {selectedRowKeys.length > 1 || selectedRowKeys.length === 0
                      ? 'itens selecionados'
                      : 'item selecionado'}
                    {' (Max. 5 itens)'}
                  </span>
                  <Button
                    name="clearSelection"
                    variant="secondary"
                    onClick={() => setSelectedRowKeys([])}
                    disabled={selectedRowKeys.length === 0}
                  >
                    Limpar Seleção
                  </Button>
                </>
              ) : null}
            </SelectionContainer>
            <ButtonContainer>
              {isViewerEmitter && (
                <Button
                  name="updateOrders"
                  variant="secondary"
                  onClick={onUpdateOrderClick}
                >
                  Solicitar Alteração
                </Button>
              )}
              <ExportOrders
                orders={filteredOrders}
                orderTab={EOrderTab.OPEN}
                disableClick={selectedRowKeys.length > 0}
              />
              {orderUserPreference && (
                <ConfigTables
                  orderUserPreference={orderUserPreference}
                  orderTab={EOrderTab.OPEN}
                />
              )}
            </ButtonContainer>
          </TableHeaderContent>
        </Container>
      )}
      <TableContainer>
        {!isViewerEmitter ? (
          <Table
            expandedColumnPosition="right"
            columns={getColumns()}
            data={openOrdersData}
            expandedRowRender={(record) => renderExpandableSection(record)}
            rowSelection={isViewerEmitter ? rowSelection : undefined}
            hasPagination
          />
        ) : (
          <Table
            expandedColumnPosition="left"
            columns={getColumns()}
            data={openOrdersData}
            rowSelection={isViewerEmitter ? rowSelection : undefined}
            expandedRowRender={(record) => renderExpandableSection(record)}
            hasPagination
          />
        )}
      </TableContainer>
      {cancelOrder && (
        <CancelOrder
          order={cancelOrder}
          customerViewType={customerViewType}
          onClose={() => setCancelOrder(null)}
        />
      )}
      {isUpdateSheetOpened && isViewerEmitter && (
        <UpdateOrderSideSheet
          onClose={onCloseSideSheet}
          tableSelectedItems={openOrdersData.filter((order) =>
            selectedRowKeys.includes(order.key)
          )}
          userOrderPreference={userOrderPreference}
          clearSelectItems={() => setSelectedRowKeys([])}
        />
      )}
    </>
  );
};

export default OpenOrdersTable;
